import type { PaletteColorOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import type React from 'react';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { components } from './components';
import { darkPalette, lightPalette } from './palette';

// Update the Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}
declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    tertiary: true;
  }
}

type Warnings = {
  minor: string;
  moderate: string;
  major: string;
};

// Mode-aware surfaces for inputs and raised elements.
type Surface = {
  input: string;
  inputHover: string;
  raised: string;
};

// High-contrast fill for selected chips and badges, e.g. dark-on-light.
type Inverted = {
  main: string;
  contrastText: string;
};

// Translucent greys that read correctly on light and dark backgrounds,
// matching SAM's `palette.getGrey(10..40)`.
type GreyAlpha = Record<10 | 20 | 30 | 40, string>;

type Graph = {
  colors: {
    primaryLine: string;
    primaryFill: string;
    secondaryLine: string;
    feedLine: string;
    background: string;
  };
};

export type BoxSizes = 'small' | 'medium' | 'large' | 'xlarge' | 'full';

type BoxSize = Record<BoxSizes, string>;

declare module '@mui/material/styles' {
  // `cssVariables` is enabled, so `theme.vars` is always defined.
  interface CssThemeVariables {
    enabled: true;
  }
  interface Theme {
    graph: Graph;
    boxSize: BoxSize;
  }
  interface Palette {
    warnings: Warnings;
    tertiary: Palette['primary'];
    white: string;
    surface: Surface;
    greyAlpha: GreyAlpha;
    inverted: Inverted;
    scrim: string;
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    graph: Graph;
    boxSize: BoxSize;
  }
  interface PaletteOptions {
    white?: string;
    offWhite?: string;
    primary?: PaletteColorOptions;
    secondary?: PaletteColorOptions;
    tertiary?: PaletteColorOptions;
    success?: PaletteColorOptions;
    info?: PaletteColorOptions;
    error?: PaletteColorOptions;
    warning?: PaletteColorOptions;
    warnings?: Warnings;
    surface?: Surface;
    greyAlpha?: GreyAlpha;
    inverted?: Inverted;
    scrim?: string;
  }

  interface TypographyVariants {
    alarmBadge: React.CSSProperties;
    tableHeader?: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    alarmBadge?: React.CSSProperties;
    tableHeader?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    alarmBadge: true;
    tableHeader: true;
  }
}
const graph = {
  colors: {
    primaryLine: '#0069AB',
    primaryFill: '#EBF7FF80',
    secondaryLine: '#BDBDBD',
    feedLine: '#FFB801',
    background: 'transparent',
  },
};

const boxSize = {
  small: '93px',
  medium: '148px',
  large: '202px',
  xlarge: '272px',
  full: '312px',
};

const typography = {
  fontFamily: 'Sandvik Sans Text',
  fontWeight: 500,
  body1: {
    fontWeight: 500,
  },
  body2: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '140%',
    letterSpacing: '0.17px',
  },
  overline: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '31.92px',
    letterSpacing: '1px',
  },
  alarmBadge: {
    fontWeight: 700,
    fontSize: 10,
  },
  tableHeader: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '24px',
    letterSpacing: '0.17px',
  },
};

// Static `theme.palette` values resolve to the light scheme. Code that should
// follow the active scheme must use `theme.vars.*` (CSS variables).
const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-mui-color-scheme' },
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette },
  },
  graph,
  boxSize,
  components,
  typography,
});

export default theme;
